import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { AddedToCartModal } from '@/components/cart/added-to-cart-modal';
import { RelatedProducts } from '@/components/product/related-products';
import { 
  Heart, 
  ChevronLeft, 
  ChevronRight,
  Plus, 
  Minus, 
  ShoppingCart,
  Package,
  Truck,
  Shield,
  AlertCircle,
  Share2,
  X,
  Check,
  Zap,
  Star,
  Clock
} from 'lucide-react';
import { Product, SelectedVariation } from '@/types';
import { useStoreInfo } from '@/lib/store';
import { useStore } from '@/store';
import { formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount, getEffectiveVariationPrice } from '@/lib/discount';
import { ProductDetailSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';

export function ModernProductDetailPageV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariations, setSelectedVariations] = useState<SelectedVariation[]>([]);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  
  const addToCart = useStore((state) => state.addToCart);
  const wishlist = useStore((state) => state.wishlist);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const { storeInfo, openLockNotice } = useStoreInfo();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          setAllProducts(data.products || []);
          const foundProduct = data.products.find((p: Product) => p.id === parseInt(id!));
          if (foundProduct) {
            setProduct(foundProduct);
            
            // Setup variations
            if (foundProduct.variations && foundProduct.variations.length > 0) {
              const normalizeVariation = (v: any) => ({
                v_id: v.v_id,
                variation: v.variation,
                quantity: 0,
                variation_amount: v.variation_amount,
                variation_img: v.variation_img,
                hasQuantityPricing: !!v.hasQuantityPricing,
                quantityPricing: Array.isArray(v.quantityPricing) ? v.quantityPricing : []
              });

              setSelectedVariations(foundProduct.variations.map(normalizeVariation));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const baseImages = (product.images || []).map(resolveImageUrl).filter((img): img is string => !!img);
    const variationImages = (product.variations || [])
      .map(v => resolveImageUrl(v.variation_img))
      .filter((img): img is string => !!img);
    const uniqueImages = Array.from(new Set([...baseImages, ...variationImages]));
    setGalleryImages(uniqueImages);
    
    // Preload images for instant switching
    uniqueImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [product]);

  const resolveImageUrl = (imagePath?: string | null) => {
    if (!imagePath) return null;
    const trimmed = imagePath.trim();
    if (trimmed.match(/^https?:\/\//i)) return trimmed;
    if (trimmed.includes('img.cultaz.com')) return trimmed;
    return `https://img.cultaz.com/${trimmed.replace(/^\/+/, '')}`;
  };

  const handleAddToCart = () => {
    if (storeInfo?.site_lock === 1) {
      openLockNotice();
      return;
    }
    if (isAdding) return;
    setIsAdding(true);
    if (!product) return;

    const hasVariations = product.variations && product.variations.length > 0;
    const hasRealVariations = hasVariations && product.variations!.some(v => v.variation.toLowerCase() !== 'nil');
    
    if (hasRealVariations) {
      const activeVariations = selectedVariations.filter(v => v.quantity > 0);
      if (activeVariations.length === 0) {
        toast.error('Please select at least one variation');
        setIsAdding(false);
        return;
      }
      addToCart({ ...product, quantity: 0, selectedVariations: activeVariations });
    } else {
      // Non-variation or Nil variation product
      // Don't override sellingprice - let calculateProductDiscount handle pricing recalculation
      // based on current quantity and variation tiers
      addToCart({
        ...product,
        quantity,
        selectedVariations: []
      });
    }

    toast.success('Added to cart', { duration: 3200, position: 'bottom-center' });
    setShowAddedToCart(true);
    setTimeout(() => setShowAddedToCart(false), 3000);
    setTimeout(() => setIsAdding(false), 1500);
  };

  const updateVariationQuantity = useCallback((variationName: string, change: number) => {
    setSelectedVariations(prev =>
      prev.map(v => {
        if (v.variation === variationName) {
          return { ...v, quantity: Math.max(0, v.quantity + change) };
        }
        return v;
      })
    );
  }, []);

  const setVariationQuantity = (variationName: string, qty: number) => {
    setSelectedVariations(prev =>
      prev.map(v => (v.variation === variationName ? { ...v, quantity: Math.max(0, qty) } : v))
    );
  };

  const handleQuantityPricingClick = (variation: any, tier: any) => {
    const maxQuantity = parseInt(tier.maxQuantity || tier.minQuantity || '1');
    setVariationQuantity(variation.variation, maxQuantity);
  };

  const isQuantityPricingSelected = (variation: any, tier: any) => {
    const selected = selectedVariations.find(v => v.variation === variation.variation);
    if (!selected) return false;
    const minQty = parseInt(tier.minQuantity || '1');
    const maxQty = parseInt(tier.maxQuantity || tier.minQuantity || '1');
    return selected.quantity >= minQty && selected.quantity <= maxQty;
  };

  const changeImage = useCallback((index: number) => {
    setCurrentImageIndex(index);
  }, []);

  const isInWishlist = product ? wishlist.includes(product.id) : false;
  const discountInfo = calculateProductDiscount(product || { sellingprice: '0' }, quantity);

  // Get effective price for Nil variation with quantity-based pricing
  const getEffectiveNilPrice = () => {
    if (!product?.variations) return product?.sellingprice || '0';
    const nilVar = product.variations.find(v => v.variation.toLowerCase() === 'nil');
    if (!nilVar || !nilVar.hasQuantityPricing || !nilVar.quantityPricing?.length) {
      return product.sellingprice;
    }
    const tier = nilVar.quantityPricing.find(t => {
      const min = parseInt(t.minQuantity || '1');
      const max = parseInt(t.maxQuantity || t.minQuantity || '1');
      return quantity >= min && quantity <= max;
    });
    return tier?.price || product.sellingprice;
  };

  if (loading) return <ProductDetailSkeleton />;
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{product.produt_name} - {storeInfo?.site_name}</title>
        <meta name="description" content={product.description || ''} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            whileHover={{ x: -3 }}
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium py-6 text-sm"
          >
            <ChevronLeft className="h-5 w-5" />
            Back
          </motion.button>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-16">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="relative group overflow-hidden rounded-2xl bg-gray-100 aspect-square flex items-center justify-center">
                {galleryImages.length > 0 ? (
                  <>
                    <img
                      src={galleryImages[currentImageIndex]}
                      alt={product.produt_name}
                      className="w-full h-full object-contain hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => setShowZoomModal(true)}
                    />
                    {galleryImages.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ChevronLeft className="h-5 w-5 text-gray-900" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ChevronRight className="h-5 w-5 text-gray-900" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-xs font-medium">
                          {currentImageIndex + 1} / {galleryImages.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <NoImagePlaceholder size="lg" />
                )}
              </div>

              {/* Thumbnail Strip */}
              {galleryImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {galleryImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => changeImage(index)}
                      className={`h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 transition-all border-2 ${
                        index === currentImageIndex
                          ? 'border-brand-500 ring-2 ring-brand-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={image} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-6"
            >
              {/* Category & Title */}
              <div>
                <p className="text-xs font-bold text-brand-600 uppercase tracking-widest mb-3">
                  {product.category || 'Product'}
                </p>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  {product.produt_name}
                </h1>
              </div>



              {/* Price Section */}
              <div className="bg-gradient-to-r from-brand-50 to-purple-50 p-4 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatPrice(product.sellingprice)}
                  </span>
                  {product.costprice && parseInt(product.costprice) > parseInt(product.sellingprice) && (
                    <span className="text-lg text-gray-500 line-through">
                      {formatPrice(product.costprice)}
                    </span>
                  )}
                </div>
                {discountInfo.hasDiscount && (
                  <p className="text-sm text-brand-600 font-semibold">
                    {discountInfo.discountType === 'percentage'
                      ? `${discountInfo.discountAmount}% OFF`
                      : `Save ${formatPrice(discountInfo.discountAmount)}`}
                  </p>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2 text-sm">
                {product.totalStock > 0 ? (
                  <>
                    <Check className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">
                      {product.totalStock} in stock
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <span className="text-red-600 font-medium">Out of stock</span>
                  </>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <p className="text-gray-700 leading-relaxed">
                  {product.description.substring(0, 200)}...
                </p>
              )}

              {/* Variations */}
              {selectedVariations.length > 0 && selectedVariations.some(v => v.variation.toLowerCase() !== 'nil') && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Available Options</h3>
                  <div className="space-y-4">
                    {selectedVariations.filter(v => v.variation.toLowerCase() !== 'nil').map((variation) => {
                      const selected = selectedVariations.find(v => v.variation === variation.variation);
                      const selectedQty = selected?.quantity || 0;
                      const basePrice = variation.variation_amount ? Number(variation.variation_amount) : Number(product.sellingprice || 0);
                      const effectivePrice = selectedQty > 0 && variation.hasQuantityPricing && variation.quantityPricing?.length
                        ? (() => {
                            const tier = variation.quantityPricing!.find(t => {
                              const min = parseInt(t.minQuantity || '1');
                              const max = parseInt(t.maxQuantity || t.minQuantity || '1');
                              return selectedQty >= min && selectedQty <= max;
                            });
                            return tier?.price ? Number(tier.price) : basePrice;
                          })()
                        : basePrice;
                      
                      return (
                        <div key={variation.variation} className="border border-gray-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900">{variation.variation}</h4>
                            <span className={`text-lg font-bold ${selectedQty > 0 ? 'text-brand-600' : 'text-gray-500'}`}>
                              {formatPrice(String(effectivePrice))}
                            </span>
                          </div>

                          {/* Quantity Pricing Tiers */}
                          {variation.hasQuantityPricing && variation.quantityPricing && variation.quantityPricing.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {variation.quantityPricing.map((tier, tierIndex) => (
                                <button
                                  key={`tier-${variation.v_id}-${tierIndex}`}
                                  onClick={() => handleQuantityPricingClick(variation, tier)}
                                  className={`shrink-0 whitespace-nowrap rounded-full border text-xs px-3 py-1.5 transition-all ${
                                    isQuantityPricingSelected(variation, tier)
                                      ? 'border-brand-500 bg-brand-500 text-white shadow-md'
                                      : 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
                                  }`}
                                >
                                  {tier.minQuantity}-{tier.maxQuantity} @ {formatPrice(tier.price)}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateVariationQuantity(variation.variation, -1)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={variation.quantity}
                              onChange={(e) => {
                                setVariationQuantity(variation.variation, Math.max(0, parseInt(e.target.value) || 0));
                              }}
                              className="w-12 text-center border border-gray-200 rounded px-2 py-1"
                            />
                            <button
                              onClick={() => updateVariationQuantity(variation.variation, 1)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            {selectedQty > 0 && effectivePrice !== basePrice && (
                              <span className="ml-auto text-xs text-gray-600">
                                {selectedQty} pcs: {formatPrice(String(effectivePrice * selectedQty))}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity (for non-variation products or Nil variation) */}
              {(!selectedVariations || selectedVariations.length === 0 || selectedVariations.every(v => v.variation.toLowerCase() === 'nil')) && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-gray-900">Quantity</h3>
                  
                  {(() => {
                    const nilVariation = product?.variations?.find(v => v.variation.toLowerCase() === 'nil');
                    const hasNilQtyPricing = nilVariation?.hasQuantityPricing && 
                      nilVariation.quantityPricing && 
                      nilVariation.quantityPricing.length > 0;
                    
                    const effectiveTier = hasNilQtyPricing 
                      ? nilVariation!.quantityPricing!.find(tier => {
                          const min = parseInt(tier.minQuantity || '1');
                          const max = parseInt(tier.maxQuantity || tier.minQuantity || '1');
                          return quantity >= min && quantity <= max;
                        })
                      : null;
                    
                    const effectivePrice = effectiveTier?.price || product?.sellingprice || '0';

                    return (
                      <>
                        {/* Quantity Pricing Tiers */}
                        {hasNilQtyPricing && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-brand-600">Select quantity for best price:</p>
                            <div className="flex flex-wrap gap-2">
                              {nilVariation!.quantityPricing!.map((tier, tierIndex) => {
                                const tierMinQty = parseInt(tier.minQuantity || '1');
                                const tierMaxQty = parseInt(tier.maxQuantity || tierMinQty.toString());
                                const isSelected = quantity >= tierMinQty && quantity <= tierMaxQty;
                                
                                return (
                                  <button
                                    key={`nil-tier-${tierIndex}`}
                                    onClick={() => setQuantity(tierMaxQty)}
                                    className={`shrink-0 whitespace-nowrap rounded-full border text-xs px-3 py-1.5 transition-all ${
                                      isSelected
                                        ? 'border-brand-500 bg-brand-500 text-white shadow-md'
                                        : 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
                                    }`}
                                  >
                                    {tier.minQuantity}-{tier.maxQuantity} @ {formatPrice(tier.price)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Quantity Selector */}
                        <div className="flex items-center gap-3 bg-white border border-brand-200 rounded-lg p-2 w-fit">
                          <button
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-16 text-center border-0 outline-none text-lg font-bold"
                          />
                          <button
                            onClick={() => setQuantity(Math.min(product?.totalStock || 999, quantity + 1))}
                            disabled={product && quantity >= product.totalStock}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Show Effective Price with Tier Info */}
                        {hasNilQtyPricing && effectiveTier && (
                          <div className="bg-brand-50 rounded-lg p-3 border border-brand-200">
                            <p className="text-sm font-semibold text-brand-900">
                              Price for {quantity} pcs: <span className="text-lg text-brand-600">{formatPrice(effectivePrice)}</span>
                              <span className="text-xs text-brand-700 ml-1">(each)</span>
                            </p>
                            <p className="text-xs text-brand-700 mt-1">
                              Total: {formatPrice(String(Number(effectivePrice) * quantity))}
                            </p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className="flex-1 bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ShoppingCart className="h-5 w-5" />
                  Add to Cart
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => product && toggleWishlist(product.id)}
                  className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                    isInWishlist
                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Heart className={`h-5 w-5 ${isInWishlist ? 'fill-current' : ''}`} />
                </motion.button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <Truck className="h-5 w-5 text-brand-600" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">Fast Delivery</p>
                    <p className="text-gray-600 text-xs">2-7 business days</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-brand-600" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">Secure Payment</p>
                    <p className="text-gray-600 text-xs">100% protected</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Related Products */}
          <div className="mt-16">
            <RelatedProducts currentProductId={product.id} allProducts={allProducts} />
          </div>
        </div>
      </div>

      {showAddedToCart && <AddedToCartModal />}
    </>
  );
}
